import ordersApi from 'api/requests/orders';
import DashCollapse from 'components/_UI/DashCollapse';
import Loader from 'components/_UI/Loader';
import Paginate from 'components/_UI/Paginate';
import Text from 'components/_UI/Text';
import currency from 'helpers/currency';
import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import OrderDetail from './OrderDetail';
import LinkPage from 'components/_UI/LinkPage';
import Icon from 'components/_UI/Icon';
import Ilustration from 'components/_UI/Ilustration';
import paths from 'routes/paths';

function PaymentMethodCell({ method }) {
    if (method === 'credit_card') {
        return (
            <PaymentMethod>
                <CreditCard size={16} color={colors.night} />
                <span>Cartão</span>
            </PaymentMethod>
        );
    }

    return (
        <PaymentMethod>
            <Column width='16px' height='16px'>
                <Icon name='pix' color={colors.night} />
            </Column>
            <span>PIX</span>
        </PaymentMethod>
    );
}

const STATUS_MAP = {
    approved: { label: 'Aprovado', bg: '#DCFCE7', color: '#166534' },
    completed: { label: 'Concluído', bg: '#DCFCE7', color: '#166534' },
    accepted: { label: 'Aceito', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    canceled: { label: 'Expirado', bg: '#FEE2E2', color: '#DC2626' },
    failed: { label: 'Negado', bg: '#FEE2E2', color: '#DC2626' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
    refunded: { label: 'Estornado', bg: '#F3F4F6', color: '#374151' },
    chargeback: { label: 'Chargeback', bg: '#FEE2E2', color: '#991B1B' },
};

export default function OrdersTable({ status, search }) {
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState([]);
    const [perPage, setPerpage] = useState(null);
    const [lastPage, setLastPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const [totalItems, setTotalItems] = useState(null);
    // `?order=ID` abre o detalhe daquele pedido (o link da notificação de estorno).
    const [searchParams, setSearchParams] = useSearchParams();
    const orderFromLink = Number(searchParams.get('order')) || null;
    const [detail, setDetail] = useState(() => (orderFromLink ? { id: orderFromLink } : undefined));

    useEffect(() => {
        if (orderFromLink) setDetail({ id: orderFromLink });
    }, [orderFromLink])

    useEffect(() => {
        getOrders();
    }, [status, search])

    function closeDetail() {
        setDetail(false);

        // Sem tirar o parâmetro, fechar e tocar de novo no mesmo link não reabriria.
        if (searchParams.has('order')) {
            const next = new URLSearchParams(searchParams);
            next.delete('order');
            setSearchParams(next, { replace: true });
        }
    }

    async function getOrders(page = 1) {
        setLoading(true);

        try {
            const { data } = await ordersApi.get({ status, page, search })
            setContent(data?.data);
            setPerpage(data?.meta.per_page)
            setLastPage(data.meta.last_page)
            setCurrentPage(data?.meta.current_page)
            setTotalItems(data?.meta.total)
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-BR');
    }

    function formatTime(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    return (
        <Container>
            {loading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                content?.length <= 0 ? (
                    <EmptyState>
                        <Column width='auto' height='240px'>
                            <Ilustration name='orders' />
                        </Column>
                        <Text marginTop='16px' name='highlight' type='bold' color={colors.nightDark}>
                            Nenhum pedido encontrado
                        </Text>
                        <Text name='small' color={colors.night} textAlign='center'>
                            Você ainda não possui pedidos nesta categoria.
                        </Text>
                    </EmptyState>
                ) : (
                    <>
                        {/* Desktop table */}
                        <DesktopTable>
                            <thead>
                                <tr>
                                    <Th>Cliente</Th>
                                    <Th>Data</Th>
                                    <Th>Valor</Th>
                                    <Th>Pagamento</Th>
                                    <Th>Evento</Th>
                                    <Th>Status</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {content.map(item => {
                                    const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;

                                    return (
                                        <Tr key={item?.id} onClick={() => setDetail(item)}>
                                            <Td>
                                                <CustomerName>{item?.customer?.name}</CustomerName>
                                            </Td>
                                            <Td>
                                                <DatePrimary>{formatDate(item?.created_at)}</DatePrimary>
                                                <DateSecondary>{formatTime(item?.created_at)}</DateSecondary>
                                            </Td>
                                            <Td>
                                                <ValueText>{currency.format(item?.total)}</ValueText>
                                            </Td>
                                            <Td>
                                                <PaymentMethodCell method={item?.payment_method} />
                                            </Td>
                                            <Td>
                                                <LinkPage isWrapper to={`${paths.dashboard.events}?detail=${item?.event?.slug}`} target={'_SELF'} onClick={(e) => e.stopPropagation()}>
                                                    <EventLink>{item?.event?.name}</EventLink>
                                                </LinkPage>
                                            </Td>
                                            <Td>
                                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                    {statusInfo.label}
                                                </StatusBadge>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </DesktopTable>

                        {/* Mobile cards */}
                        <MobileCards>
                            {content.map(item => {
                                const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;

                                return (
                                    <MobileCard key={item?.id} onClick={() => setDetail(item)}>
                                        <MobileCardHeader>
                                            <CustomerName>{item?.customer?.name}</CustomerName>
                                            <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                {statusInfo.label}
                                            </StatusBadge>
                                        </MobileCardHeader>

                                        <MobileCardBody>
                                            <MobileField>
                                                <MobileLabel>Data</MobileLabel>
                                                <MobileValue>{formatDate(item?.created_at)} - {formatTime(item?.created_at)}</MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Valor</MobileLabel>
                                                <MobileValue style={{ fontWeight: 600 }}>{currency.format(item?.total)}</MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Pagamento</MobileLabel>
                                                <PaymentMethodCell method={item?.payment_method} />
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Evento</MobileLabel>
                                                <LinkPage isWrapper to={`${paths.dashboard.events}?detail=${item?.event?.slug}`} target={'_SELF'} onClick={(e) => e.stopPropagation()}>
                                                    <EventLink>{item?.event?.name}</EventLink>
                                                </LinkPage>
                                            </MobileField>
                                        </MobileCardBody>
                                    </MobileCard>
                                );
                            })}
                        </MobileCards>

                        {lastPage > 1 && (
                            <PaginateWrapper>
                                <Paginate
                                    perPage={perPage}
                                    totalItems={totalItems}
                                    lastPage={lastPage}
                                    firstPage={1}
                                    currentPage={currentPage}
                                    marginTop={'0'}
                                    onPaginate={(number) => getOrders(number)}
                                />
                            </PaginateWrapper>
                        )}
                    </>
                )
            )}

            <DashCollapse visible={detail} onClose={closeDetail}>
                {detail && <OrderDetail data={detail} onRefunded={() => getOrders(currentPage || 1)} />}
            </DashCollapse>
        </Container>
    )
}

/* ── Layout ────────────────────────────────────── */

const Container = styled.div`
    min-height: 300px;
    display: flex;
    flex-direction: column;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const EmptyState = styled(Column)`
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const PaginateWrapper = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ── Desktop Table ─────────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: ${({ align }) => align || 'left'};
    padding: 14px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;
    cursor: pointer;

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }

    &:hover {
        background: ${colors.neutral};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

/* ── Cell Content ──────────────────────────────── */

const CustomerName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DatePrimary = styled.span`
    display: block;
    font-size: 14px;
    color: ${colors.nightDark};
`;

const DateSecondary = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
`;

const ValueText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const EventLink = styled.span`
    font-size: 13px;
    color: ${colors.primary};
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
        color: ${colors.primaryDark};
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

const PaymentMethod = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.nightDark};
`;

/* ── Mobile Cards ──────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.neutral};
        border-color: ${colors.nightLight};
    }
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;
